// Decorative SVG fragments rendered by the unicorn-land theme. Each fragment
// is positioned absolutely by the gumballs page; positions live in CSS so a
// future theme can rearrange the layout without touching markup.

export function HillBack() {
  return (
    <svg
      className="ul-deco ul-deco--hill-back"
      viewBox="0 0 800 200"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ul-hill-back" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5b2d9e" />
          <stop offset="100%" stopColor="#2d1060" />
        </linearGradient>
      </defs>
      <path
        d="M0 200 L0 90 Q200 20 400 70 T800 60 L800 200 Z"
        fill="url(#ul-hill-back)"
        opacity="0.85"
      />
    </svg>
  );
}

export function HillFront() {
  return (
    <svg
      className="ul-deco ul-deco--hill-front"
      viewBox="0 0 800 200"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ul-hill-front" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e040fb" />
          <stop offset="50%" stopColor="#c044ff" />
          <stop offset="100%" stopColor="#5b2d9e" />
        </linearGradient>
      </defs>
      <path
        d="M0 200 L0 130 Q200 60 400 100 T800 110 L800 200 Z"
        fill="url(#ul-hill-front)"
      />
    </svg>
  );
}

export function Lollipop({ x, hue, size = 1 }: { x: number; hue: number; size?: number }) {
  const r = 22 * size;
  return (
    <svg
      className="ul-deco ul-deco--lollipop"
      style={{ left: `${x}%`, transform: `scale(${size})` }}
      viewBox="0 0 80 160"
      aria-hidden="true"
    >
      <line x1="40" y1="60" x2="40" y2="160" stroke="#f0eaff" strokeWidth="3" />
      <circle cx="40" cy="50" r={r} fill={`hsl(${hue}, 90%, 65%)`} stroke="#f0eaff" strokeWidth="2" />
      <circle cx="34" cy="44" r={r * 0.35} fill="rgba(255,255,255,0.6)" />
    </svg>
  );
}

export function Star({ x, y, size }: { x: number; y: number; size: number }) {
  return (
    <span
      className="ul-deco ul-deco--star"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${size}px`,
        height: `${size}px`
      }}
      aria-hidden="true"
    />
  );
}

export function Mascot() {
  // A self-contained "unicorn" silhouette. Inline SVG keeps the asset cost at
  // zero and lets each theme own its character art without an asset pipeline.
  return (
    <svg
      className="ul-mascot"
      viewBox="0 0 240 200"
      role="presentation"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ul-mascot-body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff0fb" />
          <stop offset="100%" stopColor="#e7c8ff" />
        </linearGradient>
        <linearGradient id="ul-mascot-mane" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ff4fd8" />
          <stop offset="33%" stopColor="#ffd700" />
          <stop offset="66%" stopColor="#c8ff00" />
          <stop offset="100%" stopColor="#00e5ff" />
        </linearGradient>
      </defs>
      {/* body */}
      <ellipse cx="120" cy="135" rx="70" ry="42" fill="url(#ul-mascot-body)" />
      {/* legs */}
      <rect x="80" y="155" width="14" height="40" rx="6" fill="#fff0fb" />
      <rect x="146" y="155" width="14" height="40" rx="6" fill="#fff0fb" />
      {/* tail */}
      <path
        d="M180 130 Q220 110 215 80 Q205 100 195 105 Q200 130 180 145 Z"
        fill="url(#ul-mascot-mane)"
      />
      {/* neck + head */}
      <path
        d="M155 110 Q170 55 130 50 Q90 50 95 90 Q100 110 110 115 Z"
        fill="url(#ul-mascot-body)"
      />
      {/* mane */}
      <path
        d="M125 50 Q150 30 165 55 Q150 50 145 70 Q160 75 155 95 Q145 80 135 90 Q140 70 125 70 Z"
        fill="url(#ul-mascot-mane)"
      />
      {/* horn */}
      <path
        d="M118 50 L128 12 L138 48 Z"
        fill="#ffd700"
        stroke="#ff8c00"
        strokeWidth="1.5"
      />
      {/* eye */}
      <circle cx="118" cy="78" r="3" fill="#1a0a3a" />
      {/* sparkle on horn */}
      <circle cx="128" cy="22" r="2.5" fill="#fff" />
    </svg>
  );
}

export function unicornLandDecorations() {
  return [
    { id: "hill-back", node: <HillBack /> },
    { id: "hill-front", node: <HillFront /> },
    {
      id: "stars",
      node: (
        <>
          <Star x={5} y={8} size={2} />
          <Star x={18} y={14} size={3} />
          <Star x={32} y={6} size={2} />
          <Star x={48} y={20} size={2} />
          <Star x={64} y={10} size={3} />
          <Star x={80} y={16} size={2} />
          <Star x={92} y={6} size={2} />
          <Star x={12} y={28} size={2} />
          <Star x={70} y={24} size={2} />
        </>
      )
    },
    {
      id: "lollipops",
      node: (
        <>
          <Lollipop x={10} hue={320} size={0.85} />
          <Lollipop x={26} hue={50} size={1.05} />
          <Lollipop x={48} hue={150} size={0.95} />
          <Lollipop x={68} hue={200} size={1.0} />
          <Lollipop x={86} hue={280} size={0.9} />
        </>
      )
    },
    {
      id: "mascot",
      node: (
        <div className="ul-mascot-holder" aria-hidden="true">
          <Mascot />
        </div>
      )
    }
  ];
}
